/**
 * ====================================================================
 * FIKO CAD DIMENSION SETTINGS
 * ====================================================================
 *
 * Merkezi dimension ayarları yöneticisi
 * Preset'ler, konfigürasyon, real-time güncelleme
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import {
    DimensionVisualSettings,
    PresetName,
    STYLE_PRESETS,
    DEFAULT_VISUAL_SETTINGS,
    getStylePreset,
    mergeWithPreset,
    DimensionColorPalette,
    AnimationConfig,
    GlowEffectConfig,
    TooltipConfig,
    SmartPlacementConfig,
    ArrowStyleConfig,
    LineStyleConfig,
    LabelStyleConfig,
    ExtendedArrowStyle,
    LineStyleType
} from './visual/DimensionVisualStyle';

// ====================================================================
// SETTINGS EVENTS
// ====================================================================

/**
 * Settings change event
 */
export type SettingsChangeEvent = {
    type: 'preset' | 'colors' | 'arrow' | 'line' | 'label' | 'glow' | 'animation' | 'tooltip' | 'smartPlacement' | 'full';
    settings: DimensionVisualSettings;
    changedKeys?: string[];
};

/**
 * Settings change listener
 */
export type SettingsChangeListener = (event: SettingsChangeEvent) => void;

// ====================================================================
// DIMENSION SETTINGS
// ====================================================================

/**
 * Dimension Settings - Merkezi ayar yöneticisi
 */
export class DimensionSettings {
    // Singleton instance
    private static instance: DimensionSettings;

    // Current settings
    private settings: DimensionVisualSettings;

    // Current preset name
    private currentPreset: PresetName = 'fikoyt';

    // Change listeners
    private listeners: Set<SettingsChangeListener> = new Set();

    // LocalStorage key
    private readonly storageKey = 'fiko_dimension_settings';

    private constructor() {
        // LocalStorage'dan yükle veya default kullan
        this.settings = this.loadFromStorage() || { ...DEFAULT_VISUAL_SETTINGS };
    }

    /**
     * Singleton instance al
     */
    static getInstance(): DimensionSettings {
        if (!this.instance) {
            this.instance = new DimensionSettings();
        }
        return this.instance;
    }

    // ====================================================================
    // GETTERS
    // ====================================================================

    /**
     * Tüm ayarları al
     */
    getSettings(): DimensionVisualSettings {
        return { ...this.settings };
    }

    /**
     * Aktif preset adını al
     */
    getCurrentPreset(): PresetName {
        return this.currentPreset;
    }

    /**
     * Renk paletini al
     */
    getColors(): DimensionColorPalette {
        return { ...this.settings.colors };
    }

    /**
     * Ok ayarlarını al
     */
    getArrowConfig(): ArrowStyleConfig {
        return { ...this.settings.arrow };
    }

    /**
     * Dimension line ayarlarını al
     */
    getDimensionLineConfig(): LineStyleConfig {
        return { ...this.settings.dimensionLine };
    }

    /**
     * Extension line ayarlarını al
     */
    getExtensionLineConfig() {
        return { ...this.settings.extensionLine };
    }

    /**
     * Leader line ayarlarını al
     */
    getLeaderLineConfig() {
        return { ...this.settings.leaderLine };
    }

    /**
     * Label ayarlarını al
     */
    getLabelConfig(): LabelStyleConfig {
        return { ...this.settings.label };
    }

    /**
     * Glow ayarlarını al
     */
    getGlowConfig(): GlowEffectConfig {
        return { ...this.settings.glow };
    }

    /**
     * Animasyon ayarlarını al
     */
    getAnimationConfig(): AnimationConfig {
        return { ...this.settings.animation };
    }

    /**
     * Tooltip ayarlarını al
     */
    getTooltipConfig(): TooltipConfig {
        return { ...this.settings.tooltip };
    }

    /**
     * Smart placement ayarlarını al
     */
    getSmartPlacementConfig(): SmartPlacementConfig {
        return { ...this.settings.smartPlacement };
    }

    /**
     * Scale ayarlarını al
     */
    getScaleConfig() {
        return { ...this.settings.scale };
    }

    /**
     * Render ayarlarını al
     */
    getRenderConfig() {
        return { ...this.settings.render };
    }

    // ====================================================================
    // SETTERS
    // ====================================================================

    /**
     * Preset uygula
     */
    applyPreset(presetName: PresetName): void {
        // console.log('[DimensionSettings] applyPreset called:', presetName, 'listeners:', this.listeners.size);

        if (presetName === 'custom') {
            // Custom preset sadece mevcut ayarları korur
            this.currentPreset = 'custom';
            return;
        }

        const preset = STYLE_PRESETS[presetName];
        if (preset) {
            this.settings = { ...preset };
            this.currentPreset = presetName;
            this.saveToStorage();
            // console.log('[DimensionSettings] Notifying', this.listeners.size, 'listeners');
            this.notifyListeners({ type: 'preset', settings: this.settings });
        }
    }

    /**
     * Tüm ayarları güncelle
     */
    updateSettings(updates: Partial<DimensionVisualSettings>): void {
        this.settings = this.deepMerge(this.settings, updates);
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'full', settings: this.settings });
    }

    /**
     * Renk paletini güncelle
     */
    updateColors(colors: Partial<DimensionColorPalette>): void {
        this.settings.colors = { ...this.settings.colors, ...colors };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({
            type: 'colors',
            settings: this.settings,
            changedKeys: Object.keys(colors)
        });
    }

    /**
     * Ok ayarlarını güncelle
     */
    updateArrow(arrow: Partial<ArrowStyleConfig>): void {
        this.settings.arrow = { ...this.settings.arrow, ...arrow };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'arrow', settings: this.settings });
    }

    /**
     * Ok stilini değiştir
     */
    setArrowStyle(style: ExtendedArrowStyle): void {
        this.updateArrow({ type: style });
    }

    /**
     * Çizgi ayarlarını güncelle
     */
    updateDimensionLine(line: Partial<LineStyleConfig>): void {
        this.settings.dimensionLine = { ...this.settings.dimensionLine, ...line };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'line', settings: this.settings });
    }

    /**
     * Çizgi stilini değiştir
     */
    setLineStyle(style: LineStyleType): void {
        this.updateDimensionLine({ type: style });
    }

    /**
     * Label ayarlarını güncelle
     */
    updateLabel(label: Partial<LabelStyleConfig>): void {
        this.settings.label = { ...this.settings.label, ...label };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'label', settings: this.settings });
    }

    /**
     * Glow ayarlarını güncelle
     */
    updateGlow(glow: Partial<GlowEffectConfig>): void {
        this.settings.glow = { ...this.settings.glow, ...glow };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'glow', settings: this.settings });
    }

    /**
     * Glow'u etkinleştir/devre dışı bırak
     */
    setGlowEnabled(enabled: boolean): void {
        this.updateGlow({ enabled });
    }

    /**
     * Animasyon ayarlarını güncelle
     */
    updateAnimation(animation: Partial<AnimationConfig>): void {
        this.settings.animation = { ...this.settings.animation, ...animation };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'animation', settings: this.settings });
    }

    /**
     * Animasyonları etkinleştir/devre dışı bırak
     */
    setAnimationsEnabled(enabled: boolean): void {
        this.updateAnimation({ enabled });
    }

    /**
     * Tooltip ayarlarını güncelle
     */
    updateTooltip(tooltip: Partial<TooltipConfig>): void {
        this.settings.tooltip = { ...this.settings.tooltip, ...tooltip };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'tooltip', settings: this.settings });
    }

    /**
     * Tooltip'i etkinleştir/devre dışı bırak
     */
    setTooltipEnabled(enabled: boolean): void {
        this.updateTooltip({ enabled });
    }

    /**
     * Smart placement ayarlarını güncelle
     */
    updateSmartPlacement(smartPlacement: Partial<SmartPlacementConfig>): void {
        this.settings.smartPlacement = { ...this.settings.smartPlacement, ...smartPlacement };
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'smartPlacement', settings: this.settings });
    }

    /**
     * Smart placement'ı etkinleştir/devre dışı bırak
     */
    setSmartPlacementEnabled(enabled: boolean): void {
        this.updateSmartPlacement({ enabled });
    }

    // ====================================================================
    // QUICK TOGGLES
    // ====================================================================

    /**
     * Tüm efektleri etkinleştir
     */
    enableAllEffects(): void {
        this.settings.glow.enabled = true;
        this.settings.animation.enabled = true;
        this.settings.tooltip.enabled = true;
        this.settings.smartPlacement.enabled = true;
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'full', settings: this.settings });
    }

    /**
     * Tüm efektleri devre dışı bırak
     */
    disableAllEffects(): void {
        this.settings.glow.enabled = false;
        this.settings.animation.enabled = false;
        this.settings.tooltip.enabled = false;
        this.settings.smartPlacement.enabled = false;
        this.currentPreset = 'custom';
        this.saveToStorage();
        this.notifyListeners({ type: 'full', settings: this.settings });
    }

    /**
     * Performans modu (minimum efektler)
     */
    setPerformanceMode(enabled: boolean): void {
        if (enabled) {
            this.disableAllEffects();
            this.settings.render.antialias = false;
            this.settings.scale.mode = 'fixed';
        } else {
            this.applyPreset('fikoyt');
        }
    }

    // ====================================================================
    // PERSISTENCE
    // ====================================================================

    /**
     * LocalStorage'a kaydet
     */
    private saveToStorage(): void {
        try {
            const data = {
                settings: this.settings,
                preset: this.currentPreset,
                version: '2.0.0'
            };
            localStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (e) {
            console.warn('DimensionSettings: LocalStorage kayıt hatası', e);
        }
    }

    /**
     * LocalStorage'dan yükle
     */
    private loadFromStorage(): DimensionVisualSettings | null {
        try {
            const stored = localStorage.getItem(this.storageKey);
            if (stored) {
                const data = JSON.parse(stored);
                if (data.version === '2.0.0' && data.settings) {
                    this.currentPreset = data.preset || 'custom';
                    return data.settings;
                }
            }
        } catch (e) {
            console.warn('DimensionSettings: LocalStorage yükleme hatası', e);
        }
        return null;
    }

    /**
     * Ayarları sıfırla
     */
    resetToDefault(): void {
        this.settings = { ...DEFAULT_VISUAL_SETTINGS };
        this.currentPreset = 'fikoyt';
        this.saveToStorage();
        this.notifyListeners({ type: 'full', settings: this.settings });
    }

    /**
     * LocalStorage'ı temizle
     */
    clearStorage(): void {
        try {
            localStorage.removeItem(this.storageKey);
        } catch (e) {
            console.warn('DimensionSettings: LocalStorage temizleme hatası', e);
        }
    }

    /**
     * Public: Ayarları LocalStorage'a kaydet
     */
    saveToLocalStorage(): void {
        this.saveToStorage();
    }

    /**
     * Public: Ayarları LocalStorage'dan yükle
     */
    loadFromLocalStorage(): boolean {
        const loaded = this.loadFromStorage();
        if (loaded) {
            this.settings = loaded;
            this.notifyListeners({ type: 'full', settings: this.settings });
            return true;
        }
        return false;
    }

    // ====================================================================
    // EXPORT / IMPORT
    // ====================================================================

    /**
     * Ayarları JSON olarak export et
     */
    exportSettings(): string {
        return JSON.stringify({
            settings: this.settings,
            preset: this.currentPreset,
            version: '2.0.0',
            exportedAt: new Date().toISOString()
        }, null, 2);
    }

    /**
     * JSON'dan import et
     */
    importSettings(json: string): boolean {
        try {
            const data = JSON.parse(json);
            if (data.settings) {
                this.settings = this.deepMerge(DEFAULT_VISUAL_SETTINGS, data.settings);
                this.currentPreset = data.preset || 'custom';
                this.saveToStorage();
                this.notifyListeners({ type: 'full', settings: this.settings });
                return true;
            }
        } catch (e) {
            console.error('DimensionSettings: Import hatası', e);
        }
        return false;
    }

    // ====================================================================
    // LISTENERS
    // ====================================================================

    /**
     * Change listener ekle
     */
    addChangeListener(listener: SettingsChangeListener): void {
        this.listeners.add(listener);
        // console.log('[DimensionSettings] Listener added, total:', this.listeners.size);
    }

    /**
     * Change listener kaldır
     */
    removeChangeListener(listener: SettingsChangeListener): void {
        this.listeners.delete(listener);
    }

    /**
     * Tüm listener'ları bilgilendir
     */
    private notifyListeners(event: SettingsChangeEvent): void {
        this.listeners.forEach(listener => {
            try {
                listener(event);
            } catch (e) {
                console.error('DimensionSettings: Listener hatası', e);
            }
        });
    }

    // ====================================================================
    // UTILITY
    // ====================================================================

    /**
     * Deep merge helper
     */
    private deepMerge<T extends object>(target: T, source: Partial<T>): T {
        const result = { ...target };

        for (const key in source) {
            if (source.hasOwnProperty(key)) {
                const sourceValue = source[key];
                const targetValue = target[key];

                if (
                    sourceValue !== undefined &&
                    typeof sourceValue === 'object' &&
                    !Array.isArray(sourceValue) &&
                    sourceValue !== null &&
                    typeof targetValue === 'object' &&
                    !Array.isArray(targetValue) &&
                    targetValue !== null
                ) {
                    (result as any)[key] = this.deepMerge(targetValue as object, sourceValue as object);
                } else if (sourceValue !== undefined) {
                    (result as any)[key] = sourceValue;
                }
            }
        }

        return result;
    }

    /**
     * Mevcut preset'lerin listesi
     */
    getAvailablePresets(): { name: PresetName; label: string }[] {
        return [
            { name: 'catia', label: 'CATIA Style' },
            { name: 'solidworks', label: 'SolidWorks Style' },
            { name: 'autocad', label: 'AutoCAD Style' },
            { name: 'fikoyt', label: 'FikoYT Modern' },
            { name: 'minimal', label: 'Minimal' },
            { name: 'custom', label: 'Özel' }
        ];
    }

    /**
     * Ok stilleri listesi
     */
    getAvailableArrowStyles(): { value: ExtendedArrowStyle; label: string }[] {
        return [
            { value: ExtendedArrowStyle.FILLED, label: 'Dolu Ok' },
            { value: ExtendedArrowStyle.OPEN, label: 'Açık Ok' },
            { value: ExtendedArrowStyle.DOT, label: 'Nokta' },
            { value: ExtendedArrowStyle.TICK, label: 'Çizgi' },
            { value: ExtendedArrowStyle.ARCH, label: 'Yay' },
            { value: ExtendedArrowStyle.BOX, label: 'Kare' },
            { value: ExtendedArrowStyle.OBLIQUE, label: 'Eğik' },
            { value: ExtendedArrowStyle.SLASH, label: 'Slash' },
            { value: ExtendedArrowStyle.DOUBLE, label: 'Çift Ok' },
            { value: ExtendedArrowStyle.INTEGRAL, label: 'İntegral' },
            { value: ExtendedArrowStyle.DATUM, label: 'Datum' },
            { value: ExtendedArrowStyle.ORIGIN, label: 'Origin' },
            { value: ExtendedArrowStyle.NONE, label: 'Yok' }
        ];
    }

    /**
     * Çizgi stilleri listesi
     */
    getAvailableLineStyles(): { value: LineStyleType; label: string }[] {
        return [
            { value: LineStyleType.SOLID, label: 'Düz' },
            { value: LineStyleType.DASHED, label: 'Kesikli' },
            { value: LineStyleType.DOTTED, label: 'Noktalı' },
            { value: LineStyleType.DASH_DOT, label: 'Çizgi-Nokta' },
            { value: LineStyleType.DASH_DOT_DOT, label: 'Çizgi-Nokta-Nokta' },
            { value: LineStyleType.CENTER, label: 'Merkez' },
            { value: LineStyleType.PHANTOM, label: 'Fantom' },
            { value: LineStyleType.CONSTRUCTION, label: 'Yapı' },
            { value: LineStyleType.HIDDEN, label: 'Gizli' }
        ];
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

// Re-export types (isolatedModules uyumlu)
export type {
    DimensionVisualSettings,
    PresetName,
    DimensionColorPalette,
    AnimationConfig,
    GlowEffectConfig,
    TooltipConfig,
    SmartPlacementConfig,
    ArrowStyleConfig,
    LineStyleConfig,
    LabelStyleConfig
};

// Re-export enums (bunlar value olduğu için normal export)
export { ExtendedArrowStyle, LineStyleType };

// Re-export presets
export { STYLE_PRESETS, DEFAULT_VISUAL_SETTINGS };
